import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { useFlightDealOriginLocations } from 'home/hooks/useFlightDealLocations'
import FlightDealsCarousel from 'home/components/FlightDeals/FlightDealsCarousel/FlightDealsCarousel'
import { filterFlightDealsByAirports } from './filterFlightDealsByAirports'

const CarouselContainer = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface Props extends Omit<App.ContentfulWebPageFlightDealsCarousel, 'contentTypeId'> {
  trackingListName?: string
}

function ContentfulFlightDealsCarousel({ title, category, originAirportCodes, destinationAirportCodes, viewAllLink, trackingListName }: Props) {
  const [flightDeals, fetching] = useFlightDeals(category)

  const eligibleFlightDeals = useMemo(
    () => filterFlightDealsByAirports(flightDeals, { originAirportCodes, destinationAirportCodes }),
    [flightDeals, originAirportCodes, destinationAirportCodes],
  )

  const [airports, closestAirport] = useFlightDealOriginLocations(eligibleFlightDeals)
  const [selectedAirport, setSelectedAirport] = useState<App.Airport>()

  // Editors order the origins on purpose, so lead with the first configured one that has deals
  const defaultAirport = useMemo(() => {
    const firstConfiguredCode = originAirportCodes.find(code => airports.some(airport => airport.code === code))
    return airports.find(airport => airport.code === firstConfiguredCode) ?? closestAirport
  }, [originAirportCodes, airports, closestAirport])

  const titleNode = useMemo(() => <span dangerouslySetInnerHTML={{ __html: title }} />, [title])

  // Origins we can't resolve to an airport can't be selected, so their deals would leave an empty carousel
  if (!fetching && airports.length === 0) {
    return null
  }

  return (
    <CarouselContainer>
      <FlightDealsCarousel
        title={titleNode}
        airports={airports}
        flightDeals={eligibleFlightDeals}
        defaultAirport={defaultAirport}
        selectedAirport={selectedAirport}
        onAirportChange={setSelectedAirport}
        loading={fetching}
        showViewAll={!!viewAllLink}
        viewAllHref={viewAllLink}
        showLookingForAFlight
        trackingListName={trackingListName}
      />
    </CarouselContainer>
  )
}

export default ContentfulFlightDealsCarousel
